import type { ItemStack } from "../models/itemStack";
import type { CraftingResolverService, ItemDef } from "./craftingResolver";
import type { ChestSlotData, ItemStackDto, StockRow } from "./inventoryApi";

// Keep aligned with SlotPayload.cs.
export interface WireSlot {
    itemId: number;
    count: number;
    damage: number;
    snbt?: string | null;
}

export function fromInventorySlot(slot: WireSlot): ItemStack {
    return {
        itemId: slot.itemId,
        count: slot.count,
        damage: slot.damage,
        snbt: slot.snbt ?? undefined,
    };
}

export function fromChestSlot(slot: ChestSlotData): ItemStack {
    return {
        itemId: slot.itemId,
        count: slot.count,
        damage: slot.metadata,
        snbt: slot.snbt ?? undefined,
    };
}

export function fromItemStackDto(dto: ItemStackDto): ItemStack {
    return {
        itemId: dto.itemId,
        count: dto.count,
        damage: dto.metadata,
        snbt: dto.snbt ?? undefined,
    };
}

export function fromStockRow(row: StockRow): ItemStack {
    return {
        itemId: row.itemId,
        count: 1,
        damage: row.metadata,
        snbt: row.snbt ?? undefined,
    };
}

export function fromItemDef(def: ItemDef, count: number = 1): ItemStack {
    return {
        itemId: def.id,
        count,
        damage: def.metadata,
        identifier: `minecraft:${def.name}`,
        name: def.name,
    };
}

// Backend payload uses enchantment.level (not lvl) and lore.{lineNumber,lineContent}; normalised below.
export interface MaterialItemLike {
    materialName: string;
    materialSubId: number;
    enchantmentsJson?: string;
    loreJson?: string;
    displayName?: string;
    repairCost?: number;
}

function escapeSnbtString(s: string): string {
    return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function fromMaterialItem(m: MaterialItemLike, resolver: CraftingResolverService | null = null): ItemStack {
    const bareName = m.materialName.replace(/^minecraft:/, "");
    // Some shop sources send the 1.8 wire id ("minecraft:383") instead of a name; ItemStackView resolves those by id.
    const wireId = /^\d+$/.test(bareName) ? parseInt(bareName, 10) : undefined;
    let itemId = wireId ?? -1;
    if (resolver && wireId === undefined) {
        const items = resolver.getAllItems?.() ?? [];
        const match = items.find((i) => i.name === bareName && i.metadata === m.materialSubId);
        if (match) itemId = match.id;
    }

    const parts: string[] = [];

    if (m.enchantmentsJson) {
        try {
            const ench = JSON.parse(m.enchantmentsJson) as { id: number; level: number }[];
            if (Array.isArray(ench) && ench.length > 0) {
                const list = ench
                    .filter((e) => typeof e?.id === "number" && typeof e?.level === "number")
                    .map((e) => `{id:${e.id}s,lvl:${e.level}s}`)
                    .join(",");
                if (list.length > 0) parts.push(`ench:[${list}]`);
            }
        } catch {}
    }

    const displayFields: string[] = [];
    if (m.displayName) {
        displayFields.push(`Name:"${escapeSnbtString(m.displayName)}"`);
    }
    if (m.loreJson) {
        try {
            const lore = JSON.parse(m.loreJson) as { lineNumber: number; lineContent: string }[];
            if (Array.isArray(lore) && lore.length > 0) {
                const sorted = [...lore]
                    .filter((l) => typeof l?.lineContent === "string")
                    .sort((a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0));
                if (sorted.length > 0) {
                    const lines = sorted.map((l) => `"${escapeSnbtString(l.lineContent)}"`).join(",");
                    displayFields.push(`Lore:[${lines}]`);
                }
            }
        } catch {}
    }
    if (displayFields.length > 0) {
        parts.push(`display:{${displayFields.join(",")}}`);
    }
    if (m.repairCost) {
        parts.push(`RepairCost:${m.repairCost}`);
    }

    const snbt = parts.length > 0 ? `{${parts.join(",")}}` : undefined;

    if (wireId !== undefined) {
        return { itemId, count: 1, damage: m.materialSubId, snbt };
    }

    return {
        itemId,
        count: 1,
        damage: m.materialSubId,
        snbt,
        identifier: m.materialName.startsWith("minecraft:") ? m.materialName : `minecraft:${bareName}`,
        name: bareName,
    };
}

export function fromIdentifier(
    identifier: string,
    metadata: number = 0,
    resolver: CraftingResolverService | null = null,
): ItemStack {
    const isMinecraft = identifier.startsWith("minecraft:");
    let body = identifier.replace(/^minecraft:/, "").replace(/^minecraft_/, "");
    let damage = metadata;
    // A "name:meta" suffix (meta numeric) pins a specific block variation; strip it so the name resolves
    // and drive the variation off the metadata instead. Only for minecraft / bare names, not custom namespaces.
    if (isMinecraft || !identifier.includes(":")) {
        const metaMatch = body.match(/^(.+):(\d+)$/);
        if (metaMatch) {
            body = metaMatch[1];
            damage = parseInt(metaMatch[2], 10);
        }
    }
    const bareName = body;
    let itemId = -1;
    if (resolver) {
        const items = resolver.getAllItems?.() ?? [];
        const match = items.find((i) => i.name === bareName && i.metadata === damage)
            ?? items.find((i) => i.name === bareName);
        if (match) itemId = match.id;
    }
    const namespaced = isMinecraft || !identifier.includes(":")
        ? `minecraft:${bareName}`
        : identifier;
    return {
        itemId,
        count: 1,
        damage,
        identifier: namespaced,
        name: bareName,
    };
}
